import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Trophy, Clock, Zap, ChevronDown, LogIn, UserPlus, X, Sparkles,
  Users, Award, Lock, CheckCircle, AlertCircle, Loader2, Star, TrendingUp
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { publicAPI } from '../services/api';
import DesignCanvas from '../components/DesignCanvas';
import useUserStore from '../store/userStore';
import { generateFingerprint } from '../utils/fingerprint';

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-500',
  intermediate: 'bg-orange-500',
  advanced: 'bg-red-500',
};

// Collapsible Challenge Section Component
const CollapsibleChallengeSection = ({ title, icon: Icon, bgColor, borderColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`${bgColor} border-l-4 ${borderColor} mb-4 rounded-r-lg overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
      >
        <h3 className="font-semibold flex items-center gap-2 text-white">
          {Icon && <Icon className="w-5 h-5" />}
          {title}
        </h3>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 pt-0">{children}</div>}
    </div>
  );
};

// Blurred Leaderboard Preview Component
const LeaderboardPreview = ({ entries, isBlurred = false }) => {
  const displayEntries = entries?.length > 0 ? entries : [
    { rank: 1, username: 'DesignMaster', score: 980 },
    { rank: 2, username: 'ArchitectPro', score: 945 },
    { rank: 3, username: 'SystemsGuru', score: 920 },
  ];

  return (
    <div className="relative">
      <div className={isBlurred ? 'blur-sm select-none pointer-events-none' : ''}>
        <div className="space-y-2">
          {displayEntries.slice(0, 5).map((entry, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                index === 0
                  ? 'bg-yellow-500/20 border border-yellow-500/30'
                  : index === 1
                  ? 'bg-slate-400/20 border border-slate-400/30'
                  : index === 2
                  ? 'bg-orange-500/20 border border-orange-500/30'
                  : 'bg-slate-700/50'
              }`}
            >
              <span className={`text-lg font-bold ${
                index === 0 ? 'text-yellow-400' :
                index === 1 ? 'text-slate-300' :
                index === 2 ? 'text-orange-400' : 'text-slate-400'
              }`}>
                #{entry.rank}
              </span>
              <span className="flex-1 font-medium text-white">{entry.username}</span>
              <span className="text-indigo-400 font-semibold">{entry.score}</span>
            </div>
          ))}
        </div>
      </div>
      {isBlurred && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-slate-800/90 px-6 py-4 rounded-xl border border-slate-600 text-center">
            <Lock className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
            <p className="text-white font-semibold">Sign up to see full leaderboard</p>
            <p className="text-slate-400 text-sm">Compete with other designers!</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Partial Feedback Display Component
const PartialFeedbackDisplay = ({ feedback, onSignUp }) => {
  const gradeColors = {
    S: 'from-yellow-400 to-amber-500',
    A: 'from-green-400 to-emerald-500',
    B: 'from-blue-400 to-indigo-500',
    C: 'from-orange-400 to-amber-500',
    D: 'from-red-400 to-rose-500',
    F: 'from-gray-400 to-slate-500',
  };

  const getScoreColor = (score) => {
    if (score >= 800) return 'bg-green-500';
    if (score >= 600) return 'bg-blue-500';
    if (score >= 400) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 overflow-hidden">
      {/* Score Header - More prominent */}
      <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 p-6 border-b border-slate-700">
        <div className="flex items-center gap-6">
          <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${gradeColors[feedback.grade] || gradeColors.C} flex items-center justify-center shadow-xl ring-4 ring-white/10`}>
            <span className="text-5xl font-bold text-white drop-shadow-lg">{feedback.grade}</span>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-1">{feedback.overall_score}<span className="text-2xl text-slate-400">/1000</span></div>
            <p className="text-slate-300 text-lg">AI Evaluation Score</p>
            <p className="text-indigo-400 text-sm mt-1">Powered by Claude AI</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Shown Criteria with actual scores */}
        <div>
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            Your Scores
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {feedback.shown_criteria?.map((criteria) => {
              const score = feedback.dimension_scores?.[criteria] || Math.round(feedback.overall_score * (0.8 + Math.random() * 0.4));
              const percentage = (score / 1000) * 100;
              return (
                <div key={criteria} className="bg-slate-700/50 rounded-xl p-4 border border-slate-600">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">{criteria}</span>
                    <span className="text-indigo-400 font-bold">{score}/1000</span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-3">
                    <div
                      className={`${getScoreColor(score)} h-3 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary - if available */}
        {feedback.shown_feedback && (
          <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600">
            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-400" />
              Quick Summary
            </h4>
            <p className="text-slate-300 leading-relaxed">{feedback.shown_feedback}</p>
          </div>
        )}

        {/* Locked Criteria */}
        <div>
          <h4 className="text-slate-400 font-semibold mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Unlock More Insights
          </h4>
          <div className="flex flex-wrap gap-2">
            {feedback.locked_criteria?.map((criteria, i) => (
              <div key={i} className="px-4 py-2 bg-slate-700/30 rounded-full border border-slate-600/50 flex items-center gap-2">
                <Lock className="w-3 h-3 text-slate-500" />
                <span className="text-slate-500 text-sm">{criteria}</span>
              </div>
            ))}
            <div className="px-4 py-2 bg-slate-700/30 rounded-full border border-slate-600/50 flex items-center gap-2">
              <Lock className="w-3 h-3 text-slate-500" />
              <span className="text-slate-500 text-sm">Detailed Suggestions</span>
            </div>
            <div className="px-4 py-2 bg-slate-700/30 rounded-full border border-slate-600/50 flex items-center gap-2">
              <Lock className="w-3 h-3 text-slate-500" />
              <span className="text-slate-500 text-sm">Improvement Tips</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={onSignUp}
          className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-indigo-500/25 hover:scale-[1.02]"
        >
          <Sparkles className="w-5 h-5" />
          Unlock Full AI Feedback - Sign Up Free
        </button>
        <p className="text-center text-slate-500 text-sm">
          Get detailed feedback, compete on leaderboards, and track your progress
        </p>
      </div>
    </div>
  );
};

// Sign Up Modal Component
const SignUpModal = ({ isOpen, onClose, message }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-slate-700">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Join Buildex</h2>
          <p className="text-indigo-100">{message || 'Sign up to unlock all features!'}</p>
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="p-6">
          <div className="space-y-3">
            <button
              onClick={() => navigate('/register')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              Sign Up Free
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-600 text-slate-300 rounded-lg font-semibold hover:bg-slate-700 transition-colors"
            >
              <LogIn className="w-5 h-5" />
              Already have an account? Sign In
            </button>
          </div>
          <div className="mt-6 text-center text-sm text-slate-400">
            <p>Get access to:</p>
            <ul className="mt-2 space-y-1">
              <li>✨ Full AI-powered design evaluation</li>
              <li>🎯 20+ system design challenges</li>
              <li>🏆 Leaderboards & achievements</li>
              <li>💡 Detailed feedback on all criteria</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicChallengePage = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams(); // For /trial/:challengeId route
  const { isAuthenticated } = useUserStore();

  const [challenge, setChallenge] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [referenceSolution, setReferenceSolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [signUpMessage, setSignUpMessage] = useState('');

  // Anonymous submission state
  const [hasAttempted, setHasAttempted] = useState(false);
  const [previousResult, setPreviousResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);

  // Generate fingerprint on mount
  useEffect(() => {
    generateFingerprint().then(fp => setFingerprint(fp));
  }, []);

  // Redirect authenticated users to the regular challenge page
  useEffect(() => {
    if (isAuthenticated) {
      // If we have a specific challengeId, redirect directly
      if (challengeId) {
        navigate(`/challenge/${challengeId}`, { replace: true });
        return;
      }
      // Otherwise fetch lead magnet challenge ID and redirect
      const redirectToChallenge = async () => {
        try {
          const response = await publicAPI.getLeadMagnetChallenge();
          if (response.data.challenge?.id) {
            navigate(`/challenge/${response.data.challenge.id}`, { replace: true });
          } else {
            navigate('/challenges', { replace: true });
          }
        } catch (err) {
          navigate('/challenges', { replace: true });
        }
      };
      redirectToChallenge();
    }
  }, [isAuthenticated, navigate, challengeId]);

  const loadChallenge = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;

      if (challengeId) {
        // Fetch specific challenge by ID (for /trial/:challengeId route)
        response = await publicAPI.getChallengePreview(challengeId);
      } else {
        // Fetch lead magnet challenge (for /try route)
        response = await publicAPI.getLeadMagnetChallenge();
      }

      setChallenge(response.data.challenge);
      setStats(response.data.stats);
      setLeaderboard(response.data.leaderboard_preview || []);

      if (response.data.reference_solution?.solution_json) {
        setReferenceSolution(response.data.reference_solution.solution_json);
      }

      // Check if user already attempted
      if (response.data.challenge?.id) {
        try {
          const attemptRes = await publicAPI.checkAnonymousAttempt(
            response.data.challenge.id,
            fingerprint
          );
          if (attemptRes.data.has_attempted) {
            setHasAttempted(true);
            setPreviousResult(attemptRes.data);
          }
        } catch (err) {
          // Ignore - user hasn't attempted
        }
      }
    } catch (err) {
      console.error('Error loading challenge:', err);
      setError(challengeId ? 'Challenge not available' : 'Lead magnet challenge not available');
    } finally {
      setLoading(false);
    }
  }, [fingerprint, challengeId]);

  useEffect(() => {
    if (!isAuthenticated && fingerprint) {
      loadChallenge();
    }
  }, [isAuthenticated, loadChallenge, fingerprint]);

  // Handle anonymous design submission
  const handleSubmitDesign = async (designData) => {
    if (hasAttempted) {
      setShowSignUpModal(true);
      setSignUpMessage('You\'ve used your free attempt! Sign up to submit more designs and get full feedback.');
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await publicAPI.submitAnonymousDesign(challenge.id, {
        design_json: designData.design_json,
        component_list: designData.component_list || [],
        fingerprint,
        use_ai: designData.use_ai !== false, // Respect the AI toggle
      });

      setSubmissionResult(response.data);
      setHasAttempted(true);

      if (response.data.already_submitted) {
        setPreviousResult(response.data);
      }
    } catch (err) {
      console.error('Submission error:', err);
      setShowSignUpModal(true);
      setSignUpMessage('Something went wrong. Sign up for a better experience!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything while redirecting authenticated users
  if (isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">Redirecting...</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-slate-400 mb-4">{error || 'Challenge not available'}</p>
          <Link
            to="/register"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            Sign Up to Access All Challenges
          </Link>
        </div>
      </div>
    );
  }

  const requirements = typeof challenge.requirements === 'string'
    ? JSON.parse(challenge.requirements)
    : challenge.requirements;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Navbar */}
      <nav className="bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="text-xl font-bold text-white">buildex.dev</span>
            </Link>

            <div className="flex items-center gap-3">
              <Link
                to="/challenges"
                className="px-4 py-2 text-slate-300 hover:text-white font-medium"
              >
                Challenges
              </Link>
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white font-medium"
              >
                <LogIn className="w-4 h-4" />
                Sign In
              </Link>
              <Link
                to="/register"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                <UserPlus className="w-4 h-4" />
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Banner */}
          <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl border border-indigo-500/30 p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 font-semibold">FREE CHALLENGE</span>
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Test Your System Design Skills
                </h1>
                <p className="text-slate-300 mb-4">
                  Get AI-powered feedback on your design - completely free, no account required!
                </p>
                {!hasAttempted && (
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      1 Free AI Evaluation
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400" />
                      Instant Results
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Cards */}
              {stats && (
                <div className="flex gap-4">
                  <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center min-w-[100px]">
                    <Users className="w-6 h-6 text-indigo-400 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-white">{stats.total_attempts || 0}</div>
                    <div className="text-xs text-slate-400">Attempts</div>
                  </div>
                  <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center min-w-[100px]">
                    <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-white">{stats.avg_score || 0}</div>
                    <div className="text-xs text-slate-400">Avg Score</div>
                  </div>
                  <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-center min-w-[100px]">
                    <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-white">{stats.top_score || 0}</div>
                    <div className="text-xs text-slate-400">Top Score</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Main Content - Challenge & Canvas */}
            <div className="space-y-6">
              {/* Challenge Info Card */}
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-2 text-white">{challenge.title}</h2>
                    <p className="text-slate-400 mb-4">{challenge.description}</p>

                    <div className="flex items-center gap-4 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-sm font-medium capitalize ${
                          DIFFICULTY_COLORS[challenge.difficulty]
                        }`}
                      >
                        {challenge.difficulty}
                      </span>

                      <div className="flex items-center gap-1 text-slate-400">
                        <Trophy className="w-4 h-4" />
                        <span className="text-sm font-medium">{challenge.base_reward} DP Reward</span>
                      </div>

                      {challenge.estimated_time_minutes && (
                        <div className="flex items-center gap-1 text-slate-400">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">{challenge.estimated_time_minutes} min</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Story Text - Collapsible */}
                <CollapsibleChallengeSection
                  title="Scenario"
                  bgColor="bg-blue-500/10"
                  borderColor="border-blue-500"
                  defaultOpen={true}
                >
                  <div className="prose prose-sm max-w-none prose-invert">
                    <ReactMarkdown
                      components={{
                        h2: ({children}) => <h4 className="text-lg font-bold text-white mt-4 mb-2">{children}</h4>,
                        h3: ({children}) => <h5 className="text-base font-semibold text-slate-300 mt-3 mb-1">{children}</h5>,
                        p: ({children}) => <p className="mb-3 leading-relaxed text-slate-300">{children}</p>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                        li: ({children}) => <li className="text-slate-300">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-white">{children}</strong>,
                        code: ({children}) => <code className="bg-blue-500/20 px-1.5 py-0.5 rounded text-blue-300 text-sm">{children}</code>,
                      }}
                    >
                      {challenge.story_text}
                    </ReactMarkdown>
                  </div>
                </CollapsibleChallengeSection>

                {/* Requirements - Collapsible */}
                {requirements && (
                  <CollapsibleChallengeSection
                    title="Requirements"
                    icon={Zap}
                    bgColor="bg-yellow-500/10"
                    borderColor="border-yellow-500"
                    defaultOpen={false}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(requirements).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-slate-400 capitalize">
                            {key.replace(/_/g, ' ')}:
                          </span>{' '}
                          <span className="font-semibold text-white">
                            {typeof value === 'number' ? value.toLocaleString() : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleChallengeSection>
                )}

                {/* Tags */}
                {challenge.tags && challenge.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {challenge.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Design Canvas or Results */}
              {submissionResult?.feedback || previousResult?.feedback ? (
                <PartialFeedbackDisplay
                  feedback={submissionResult?.feedback || previousResult?.feedback}
                  onSignUp={() => {
                    setSignUpMessage('Sign up to unlock full feedback, all criteria scores, and personalized improvement tips!');
                    setShowSignUpModal(true);
                  }}
                />
              ) : (
                <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">Design Your Solution</h2>
                    {hasAttempted && (
                      <span className="text-yellow-400 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Free attempt used
                      </span>
                    )}
                  </div>

                  <DesignCanvas
                    challenge={challenge}
                    onSubmit={handleSubmitDesign}
                    initialDesign={referenceSolution}
                    referenceSolution={referenceSolution}
                    isPublicPreview={true}
                    onLoginRequired={() => {
                      setSignUpMessage('Sign up to submit your design and earn Design Points!');
                      setShowSignUpModal(true);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Bottom CTA Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Leaderboard Preview */}
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  Top Designers
                </h3>
                <LeaderboardPreview entries={leaderboard} isBlurred={true} />
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-6 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-6 h-6" />
                  <h3 className="text-lg font-bold">Ready for More?</h3>
                </div>
                <p className="text-indigo-100 mb-4 text-sm">
                  Join thousands of developers mastering system design with AI-powered feedback.
                </p>
                <ul className="space-y-2 mb-4 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    20+ Real Interview Challenges
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Detailed AI Feedback
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Track Your Progress
                  </li>
                </ul>
                <Link
                  to="/register"
                  className="block w-full py-3 bg-white text-indigo-700 font-bold rounded-lg hover:bg-indigo-50 transition-colors text-center"
                >
                  Start Learning Free
                </Link>
                <p className="text-center text-indigo-200 text-xs mt-3">
                  No credit card required • Free tier includes 2 AI evaluations/day
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Up Modal */}
      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        message={signUpMessage}
      />
    </div>
  );
};

export default PublicChallengePage;
